// @ts-check
/**
 * WASSER E-commerce Platform - Shared CurrencyDisplay Component
 *
 * Унифицированный компонент для отображения цен с конвертацией валют.
 * Используется в Client и Admin приложениях.
 *
 * @example
 * // Простое использование
 * <CurrencyDisplay amount={1000} />
 *
 * // С показом эквивалентов
 * <CurrencyDisplay amount={1000} showEquivalents equivalents={['USD', 'RUB']} />
 *
 * // С раскрывающимся списком всех валют
 * <CurrencyDisplay amount={1000} showAllCurrencies />
 */
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Box,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Paper,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';

/**
 * @typedef {Object} CurrencyInfo
 * @property {string} symbol
 * @property {string} [name]
 * @property {string} [flag]
 */

/**
 * @typedef {'KGS'|'USD'|'RUB'|'EUR'|'CNY'|'KZT'|'UZS'} CurrencyCode
 */

/**
 * Rates are stored as map currency->KGS rate. We intentionally keep it permissive because
 * API may include extra codes and checkJs+strict should not explode on index access.
 * @typedef {Record<string, number>} CurrencyRates
 */

/**
 * @typedef {Object} FormatCurrencyOptions
 * @property {boolean} [showDecimals]
 * @property {boolean} [showSymbol]
 * @property {boolean} [showFlag]
 */

// Данные о валютах (общие для всех использований)
export const CURRENCY_DATA = {
  KGS: { symbol: 'с', name: 'Кыргызский сом', flag: '🇰🇬' },
  USD: { symbol: '$', name: 'Доллар США', flag: '🇺🇸' },
  RUB: { symbol: '₽', name: 'Российский рубль', flag: '🇷🇺' },
  EUR: { symbol: '€', name: 'Евро', flag: '🇪🇺' },
  CNY: { symbol: '¥', name: 'Китайский юань', flag: '🇨🇳' },
  KZT: { symbol: '₸', name: 'Казахстанский тенге', flag: '🇰🇿' },
  UZS: { symbol: 'сўм', name: 'Узбекский сум', flag: '🇺🇿' },
};

/** @type {Record<string, CurrencyInfo>} */
const CURRENCY_DATA_MAP = CURRENCY_DATA;

/**
 * Форматирует сумму в указанной валюте
 * @param {number} amount - Сумма для форматирования
 * @param {string} currencyCode - Код валюты (KGS, USD, etc.)
 * @param {FormatCurrencyOptions} options - Опции форматирования
 * @returns {string} - Отформатированная строка
 */
export const formatCurrencyAmount = (amount, currencyCode, options = {}) => {
  const { showDecimals = false, showSymbol = true, showFlag = false } = options;
  /** @type {CurrencyInfo} */
  const info = CURRENCY_DATA_MAP[String(currencyCode)] || {
    symbol: String(currencyCode),
  };

  const numAmount = Number(amount);
  if (isNaN(numAmount)) {
    return `0 ${info.symbol}`;
  }

  let formatted;
  if (showDecimals) {
    formatted = numAmount.toLocaleString('ru-RU', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  } else {
    formatted = Math.round(numAmount).toLocaleString('ru-RU');
  }

  const parts = [];
  if (showFlag && info.flag) parts.push(info.flag);
  parts.push(formatted);
  if (showSymbol) parts.push(info.symbol);

  return parts.join(' ');
};

/**
 * Конвертирует сумму между валютами
 * @param {number} amount - Сумма для конвертации
 * @param {string} fromCurrency - Исходная валюта
 * @param {string} toCurrency - Целевая валюта
 * @param {CurrencyRates | null | undefined} rates - Курсы валют относительно KGS
 * @returns {number} - Конвертированная сумма
 */
export const convertCurrency = (amount, fromCurrency, toCurrency, rates) => {
  if (!rates || fromCurrency === toCurrency) return amount;

  // Сначала конвертируем в KGS
  let amountInKGS = amount;
  const fromRate = rates[String(fromCurrency)];
  if (fromCurrency !== 'KGS' && typeof fromRate === 'number') {
    amountInKGS = amount * fromRate;
  }

  // Затем из KGS в целевую валюту
  if (toCurrency === 'KGS') return amountInKGS;
  const toRate = rates[String(toCurrency)];
  if (typeof toRate === 'number' && toRate !== 0) {
    return amountInKGS / toRate;
  }

  return amountInKGS;
};

/**
 * CurrencyDisplay Component
 */
/**
 * @typedef {Object} CurrencyDisplayProps
 * @property {number} amount
 * @property {string} [currency]
 * @property {string} [baseCurrency]
 * @property {string} [displayCurrency]
 * @property {boolean} [showISO]
 * @property {boolean} [showEquivalents]
 * @property {string[]} [equivalents]
 * @property {boolean} [showAllCurrencies]
 * @property {boolean} [showDecimals]
 * @property {unknown} [variant]
 * @property {unknown} [color]
 * @property {object} [sx]
 * @property {CurrencyRates | null} [externalRates]
 * @property {boolean} [externalLoading]
 * @property {string | null} [externalError]
 * @property {((force?: boolean) => void) | null} [externalRefresh]
 * @property {string | null} [externalAsOfDate]
 * @property {boolean} [externalIsStale]
 * @property {string} [apiBaseUrl]
 * @property {Record<string, string>} [translations]
 */

/** @param {CurrencyDisplayProps} props */
const CurrencyDisplay = ({
  // Основные props
  amount,
  currency = 'KGS',
  baseCurrency,
  displayCurrency,

  // Опции отображения
  showISO = false,
  showEquivalents = false,
  equivalents = ['USD', 'RUB'],
  showAllCurrencies = false,
  showDecimals = false,

  // UI props
  variant = 'h6',
  color = 'textPrimary',
  sx = {},

  // Опциональные внешние данные (для интеграции с Context)
  externalRates = null,
  externalLoading = false,
  externalError = null,
  externalRefresh = null,
  externalAsOfDate = null,
  externalIsStale = false,

  // API для standalone использования (без контекста)
  apiBaseUrl = '',

  // i18n
  translations = {
    loading: 'Загрузка...',
    error: 'Ошибка загрузки курсов',
    refresh: 'Обновить курсы',
    refreshing: 'Обновление...',
    nbkrTitle: 'По курсу НБКР',
    official: 'Официальные курсы Национального банка КР',
    showOther: 'Показать в других валютах',
    stale: 'Устаревшие данные',
    updatedAt: 'Обновлено: {{date}}',
  },
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [expanded, setExpanded] = useState(false);
  /** @type {[CurrencyRates | null, (v: CurrencyRates | null) => void]} */
  const [internalRates, setInternalRates] = useState(
    /** @type {CurrencyRates | null} */ (null)
  );
  const [internalLoading, setInternalLoading] = useState(false);
  const [internalError, setInternalError] = useState(
    /** @type {string | null} */ (null)
  );

  // Используем внешние или внутренние данные
  const rates = externalRates || internalRates;
  const loading = externalLoading || internalLoading;
  const error = externalError || internalError;
  const refresh = externalRefresh;
  const asOfDate = externalAsOfDate;
  const isStale = externalIsStale;

  // Загрузка курсов (только если нет внешних данных)
  const fetchRates = useCallback(async () => {
    if (externalRates || !apiBaseUrl) return;

    try {
      setInternalLoading(true);
      setInternalError(null);

      const response = await fetch(
        `${apiBaseUrl}/api/rates?codes=${Object.keys(CURRENCY_DATA).join(',')}`
      );
      const data = /** @type {{ rates?: CurrencyRates }} */ (
        await response.json()
      );

      if (data?.rates) {
        setInternalRates(data.rates);
      } else {
        throw new Error('Invalid rates response');
      }
    } catch (err) {
      console.error('[CurrencyDisplay] Failed to fetch rates:', err);
      setInternalError(err instanceof Error ? err.message : String(err));
    } finally {
      setInternalLoading(false);
    }
  }, [apiBaseUrl, externalRates]);

  // Загружаем курсы при раскрытии или если нужны эквиваленты
  useEffect(() => {
    if ((expanded || showEquivalents) && !rates && !loading && apiBaseUrl) {
      fetchRates();
    }
  }, [expanded, showEquivalents, rates, loading, apiBaseUrl, fetchRates]);

  // Определяем исходную и целевую валюты
  const fromCurrency = baseCurrency || displayCurrency || currency || 'KGS';
  const toCurrency = displayCurrency || currency || 'KGS';

  // Форматирование с конвертацией
  const formatWithConvert = (
    /** @type {number} */ amt,
    /** @type {string} */ targetCurrency,
    /** @type {string} */ sourceCurrency = fromCurrency
  ) => {
    const converted = convertCurrency(
      amt,
      sourceCurrency,
      targetCurrency,
      rates
    );
    return formatCurrencyAmount(converted, targetCurrency, { showDecimals });
  };

  const primaryAmount = formatWithConvert(amount, toCurrency);

  // Keep these permissive for checkJs: MUI typings are stricter than our dynamic usage.
  /** @type {any} */
  const typographyVariant = variant;
  /** @type {any} */
  const typographyColor = color;

  return (
    <Box sx={{ display: 'inline-block' }}>
      {/* Основная цена */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography
          component="span"
          variant={typographyVariant}
          color={typographyColor}
          sx={{
            fontWeight: 600,
            fontSize: isMobile ? '1rem' : undefined,
            whiteSpace: 'nowrap',
            ...sx,
          }}
        >
          {primaryAmount}
          {showISO && ` (${toCurrency})`}
        </Typography>

        {showAllCurrencies && (
          <Tooltip title={translations.showOther}>
            <IconButton
              aria-label={translations.showOther}
              size="small"
              onClick={() => setExpanded(!expanded)}
              sx={{
                minWidth: 44,
                minHeight: 44,
                transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.3s ease',
              }}
            >
              <ExpandMoreIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Эквиваленты в других валютах */}
      {showEquivalents && rates && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mt: 0.25 }}
        >
          {equivalents.map((code, idx) => {
            if (code === toCurrency) return null;
            const text = formatWithConvert(amount, code);
            return (
              <span key={code}>
                {idx > 0 ? ' · ' : '≈ '}
                {text}
              </span>
            );
          })}
        </Typography>
      )}

      {/* Раскрывающийся блок со всеми валютами */}
      <Collapse in={expanded}>
        <Paper
          sx={{
            mt: 1,
            p: 2,
            backgroundColor: theme.palette.grey[50],
            border: `1px solid ${theme.palette.grey[200]}`,
            borderRadius: 2,
          }}
        >
          {/* Заголовок */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <CurrencyExchangeIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography
              variant="subtitle2"
              color="primary"
              sx={{ flexGrow: 1 }}
            >
              {translations.nbkrTitle}
            </Typography>
            {refresh && (
              <Tooltip
                title={loading ? translations.refreshing : translations.refresh}
              >
                <span>
                  <IconButton
                    aria-label={
                      loading ? translations.refreshing : translations.refresh
                    }
                    size="small"
                    disabled={loading}
                    onClick={() => refresh(true)}
                    sx={{ minWidth: 44, minHeight: 44 }}
                  >
                    {loading ? (
                      <CircularProgress size={16} />
                    ) : (
                      <RefreshIcon fontSize="small" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Box>

          {/* Дата обновления */}
          {asOfDate && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mb: 1 }}
            >
              {translations.updatedAt.replace('{{date}}', asOfDate)}
              {isStale && (
                <span
                  style={{ color: theme.palette.error.main, marginLeft: 8 }}
                >
                  • {translations.stale}
                </span>
              )}
            </Typography>
          )}

          {/* Контент */}
          {loading && !rates ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="body2">{translations.loading}</Typography>
            </Box>
          ) : error && !rates ? (
            <Typography variant="body2" color="error">
              {translations.error}
            </Typography>
          ) : rates ? (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 1fr' },
                gap: 1,
              }}
            >
              {Object.keys(CURRENCY_DATA).map((code) => {
                if (code === toCurrency) return null;
                const info = CURRENCY_DATA_MAP[code];
                const converted = formatWithConvert(amount, code);

                return (
                  <Chip
                    key={code}
                    size="small"
                    label={`${info.flag} ${converted}`}
                    sx={{
                      justifyContent: 'flex-start',
                      '& .MuiChip-label': {
                        fontSize: '0.75rem',
                        fontWeight: 500,
                      },
                    }}
                  />
                );
              })}
            </Box>
          ) : null}

          {/* Подпись */}
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 1.5, display: 'block' }}
          >
            {translations.official}
          </Typography>
        </Paper>
      </Collapse>
    </Box>
  );
};

export default CurrencyDisplay;
